import React from 'react';
import { Briefcase, Globe, GraduationCap, Linkedin, Mail, Star, Twitter } from 'lucide-react';
import { Modal } from './ui';
import { Instructor } from '../types';

interface InstructorProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  instructor: Instructor | null;
}

const SOCIAL_LINK =
  'inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-text-secondary transition-colors duration-fast hover:bg-background-alt hover:text-text-primary';

/** Section heading inside the profile — sans, since this sits inside the product. */
const SectionTitle = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <h3 className="mb-3 flex items-center gap-2 font-sans text-caption font-semibold uppercase tracking-wide text-text-secondary">
    {icon}
    {children}
  </h3>
);

/**
 * Instructor profile. Previously a MUI `Dialog` with a frosted-glass gradient
 * paper and hardcoded brand colours; it is now the shared `Modal`, which brings
 * the focus trap, scroll lock and Escape handling it did not have consistently.
 */
const InstructorProfileModal: React.FC<InstructorProfileModalProps> = ({ isOpen, onClose, instructor }) => {
  if (!instructor) return null;

  const rating = instructor.rating;

  return (
    <Modal open={isOpen} onClose={onClose} title="Instructor profile" size="lg">
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
        <img
          src={instructor.photoURL || '/default-avatar.png'}
          alt=""
          width={96}
          height={96}
          loading="lazy"
          className="h-24 w-24 shrink-0 rounded-full border border-border object-cover"
        />

        <div className="min-w-0 text-center sm:text-left">
          <p className="font-sans text-h4 font-semibold text-text-primary">{instructor.displayName}</p>
          <p className="mt-0.5 break-words text-caption text-text-secondary">{instructor.email}</p>

          {rating !== undefined && (
            <p className="mt-2 flex items-center justify-center gap-1 sm:justify-start">
              <span className="sr-only">Rated {rating.toFixed(1)} out of 5</span>
              {Array.from({ length: 5 }, (_, index) => (
                <Star
                  key={index}
                  size={15}
                  aria-hidden="true"
                  className={index < Math.round(rating) ? 'fill-gold text-gold' : 'text-border'}
                />
              ))}
              <span aria-hidden="true" className="ml-1 text-caption tabular-nums text-text-secondary">
                {rating.toFixed(1)}
              </span>
            </p>
          )}

          <div className="mt-4 flex items-center justify-center gap-2 sm:justify-start">
            {instructor.socialLinks?.twitter && (
              <a
                href={instructor.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${instructor.displayName} on X`}
                className={SOCIAL_LINK}
              >
                <Twitter size={17} aria-hidden="true" />
              </a>
            )}
            {instructor.socialLinks?.linkedin && (
              <a
                href={instructor.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${instructor.displayName} on LinkedIn`}
                className={SOCIAL_LINK}
              >
                <Linkedin size={17} aria-hidden="true" />
              </a>
            )}
            {instructor.socialLinks?.website && (
              <a
                href={instructor.socialLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${instructor.displayName}'s website`}
                className={SOCIAL_LINK}
              >
                <Globe size={17} aria-hidden="true" />
              </a>
            )}
            <a href={`mailto:${instructor.email}`} aria-label={`Email ${instructor.displayName}`} className={SOCIAL_LINK}>
              <Mail size={17} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <SectionTitle icon={<GraduationCap size={16} aria-hidden="true" />}>About</SectionTitle>
        <p className="max-w-prose text-body text-text-secondary">
          {instructor.about || 'No “About” section provided.'}
        </p>
      </div>

      {instructor.education && instructor.education.length > 0 && (
        <div className="mt-8 border-t border-border pt-6">
          <SectionTitle icon={<GraduationCap size={16} aria-hidden="true" />}>Education</SectionTitle>
          <ul className="divide-y divide-border">
            {instructor.education.map((edu, index) => (
              <li key={index} className="py-3 first:pt-0 last:pb-0">
                <p className="font-sans text-body font-medium text-text-primary">{edu.degree}</p>
                <p className="mt-0.5 text-caption text-text-secondary">
                  {edu.institution} ({edu.year})
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {instructor.workExperience && instructor.workExperience.length > 0 && (
        <div className="mt-8 border-t border-border pt-6">
          <SectionTitle icon={<Briefcase size={16} aria-hidden="true" />}>Work experience</SectionTitle>
          <ul className="divide-y divide-border">
            {instructor.workExperience.map((work, index) => (
              <li key={index} className="py-3 first:pt-0 last:pb-0">
                <p className="font-sans text-body font-medium text-text-primary">
                  {work.title} at {work.company}
                </p>
                <p className="mt-0.5 text-caption text-text-secondary">{work.years}</p>
                {work.description && <p className="mt-1.5 text-caption text-text-secondary">{work.description}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
};

export default InstructorProfileModal;
